import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import Links from '@/shared/constants/navegacao';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Collapse } from 'react-bootstrap';
import {
  EncerrarSessao,
  EncerrarTodasSessoes,
  EncerrarTodasSessoesDoSistema,
  ListarHistoricoLogins,
  UsuarioLoginDto,
} from './sessoes.service';
import ModalHistoricoSessoesUsuario from './ModalHistoricoSessoesUsuario';
import { BiArrowBack, BiInfoCircle, BiLogOut, BiSearch, BiXCircle } from 'react-icons/bi';

const LIMITE_SESSOES_ATIVAS = 2;
const LIMITE_REGISTROS_SESSAO = 5;

type GrupoSessoes = {
  usuarioId: number;
  username: string;
  nome?: string;
  sessoes: UsuarioLoginDto[];
};

function dataReferenciaSessao(sessao: UsuarioLoginDto): number {
  const raw = sessao.ultimoAcesso || sessao.logadoEm;
  return raw ? new Date(raw).getTime() : 0;
}

function tituloIpUserAgent(sessao: UsuarioLoginDto) {
  const partes = [sessao.ipOrigem, sessao.userAgent].filter(Boolean);
  return partes.length > 0 ? partes.join('\n') : undefined;
}

function agruparPorUsuario(sessoes: UsuarioLoginDto[]): GrupoSessoes[] {
  const mapa = new Map<number, GrupoSessoes>();

  for (const sessao of sessoes) {
    const usuarioId = sessao.usuario.id;
    const existente = mapa.get(usuarioId);
    if (existente) {
      existente.sessoes.push(sessao);
    } else {
      mapa.set(usuarioId, {
        usuarioId,
        username: sessao.usuario.username,
        nome: sessao.usuario.pessoa?.nome,
        sessoes: [sessao],
      });
    }
  }

  const grupos = Array.from(mapa.values())
    .map((grupo) => {
      const sessoesOrdenadas = [...grupo.sessoes].sort(
        (a, b) => dataReferenciaSessao(b) - dataReferenciaSessao(a),
      );
      return {
        ...grupo,
        sessoes: sessoesOrdenadas.slice(0, LIMITE_REGISTROS_SESSAO),
      };
    })
    .filter((grupo) => grupo.sessoes.some((sessao) => sessao.ativo));

  return grupos.sort((a, b) => {
    const diff = dataReferenciaSessao(b.sessoes[0]) - dataReferenciaSessao(a.sessoes[0]);
    if (diff !== 0) return diff;
    return a.username.localeCompare(b.username, 'pt-BR');
  });
}

export default function SessoesPage() {
  const { setActions } = useBreadcrumbActions();
  const [sessoes, setSessoes] = useState<UsuarioLoginDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [encerrando, setEncerrando] = useState<number | null>(null);
  const [encerrandoSistema, setEncerrandoSistema] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [recolhidos, setRecolhidos] = useState<Set<number>>(() => new Set());

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const dados = await ListarHistoricoLogins(undefined, 2000);
      setSessoes(dados);
    } catch (error: any) {
      console.error('Erro ao carregar sessões:', error);
      toast.error(error?.message || 'Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handleEncerrarTodasSessoesDoSistema = useCallback(async () => {
    const ok = window.confirm(
      'Tem certeza que deseja forçar o logout de TODOS os usuários do sistema?\n\n' +
        'Sua sessão atual será mantida. As demais sessões ativas serão encerradas.',
    );
    if (!ok) return;

    try {
      setEncerrandoSistema(true);
      await EncerrarTodasSessoesDoSistema();
      toast.success('Todas as demais sessões ativas foram encerradas.');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar todas as sessões:', error);
      toast.error(error?.message || 'Erro ao encerrar todas as sessões');
    } finally {
      setEncerrandoSistema(false);
    }
  }, [carregarDados]);

  useEffect(() => {
    setActions({
      primary: { label: 'Atualizar', onClick: () => carregarDados() },
      secondary: {
        label: 'Voltar para Auditorias',
        onClick: () => {},
        href: Links.Auditorias_Index,
        icon: BiArrowBack,
      },
      tertiary: {
        label: 'Histórico por usuário',
        onClick: () => setModalHistoricoAberto(true),
        icon: BiSearch,
      },
      print: {
        label: encerrandoSistema ? 'Encerrando…' : 'Deslogar todos os usuários',
        onClick: () => {
          void handleEncerrarTodasSessoesDoSistema();
        },
        icon: BiLogOut,
        disabled: encerrandoSistema,
      },
    });
    return () => setActions({});
  }, [setActions, carregarDados, encerrandoSistema, handleEncerrarTodasSessoesDoSistema]);

  const handleEncerrarSessao = async (sessaoId: number) => {
    if (!window.confirm('Tem certeza que deseja encerrar esta sessão?')) {
      return;
    }

    try {
      setEncerrando(sessaoId);
      await EncerrarSessao(sessaoId);
      toast.success('Sessão encerrada com sucesso!');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar sessão:', error);
      toast.error(error?.message || 'Erro ao encerrar sessão');
    } finally {
      setEncerrando(null);
    }
  };

  const handleEncerrarTodasSessoes = async (usuarioId: number) => {
    if (!window.confirm('Tem certeza que deseja encerrar todas as sessões ativas deste usuário?')) {
      return;
    }

    try {
      setEncerrando(usuarioId);
      await EncerrarTodasSessoes(usuarioId);
      toast.success('Todas as sessões ativas do usuário foram encerradas!');
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao encerrar sessões:', error);
      toast.error(error?.message || 'Erro ao encerrar sessões');
    } finally {
      setEncerrando(null);
    }
  };

  const formatarData = (data?: string | null) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const grupos = agruparPorUsuario(sessoes);
  const totalAtivas = grupos.reduce(
    (acc, grupo) => acc + grupo.sessoes.filter((s) => s.ativo).length,
    0,
  );

  const estaAberto = (usuarioId: number) => !recolhidos.has(usuarioId);

  const alternarCard = (usuarioId: number) => {
    setRecolhidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(usuarioId)) proximo.delete(usuarioId);
      else proximo.add(usuarioId);
      return proximo;
    });
  };

  const expandirTodos = () => setRecolhidos(new Set());
  const recolherTodos = () => setRecolhidos(new Set(grupos.map((grupo) => grupo.usuarioId)));

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h3 className="mb-3">Controle de Sessões de Login</h3>
          <span className="blockquote-footer pb-3 text-end">
            Usuários com sessão ativa; até {LIMITE_REGISTROS_SESSAO} registros recentes por usuário.
          </span>
          <div className="alert alert-secondary d-flex align-items-start gap-2" role="status">
            <BiInfoCircle size={22} className="flex-shrink-0 mt-1" />
            <div>
              Cada usuário pode manter no máximo <strong>{LIMITE_SESSOES_ATIVAS} sessões ativas</strong>. A lista
              exibe só quem tem ao menos uma ativa (com as últimas {LIMITE_REGISTROS_SESSAO} sessões). Use{' '}
              <strong>Histórico por usuário</strong> para consultar quem não aparece aqui.
            </div>
          </div>
          <hr />
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : grupos.length === 0 ? (
        <div className="alert alert-info" role="alert">
          Nenhuma sessão ativa no momento.
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <p className="text-muted small mb-0">
              {totalAtivas} ativa(s) · {grupos.length} usuário(s)
            </p>
            <div className="btn-group btn-group-sm" role="group" aria-label="Expandir ou recolher cards">
              <button type="button" className="btn btn-outline-secondary" onClick={expandirTodos}>
                Expandir todos
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={recolherTodos}>
                Recolher todos
              </button>
            </div>
          </div>
          <div className="d-flex flex-column gap-3">
            {grupos.map((grupo) => {
              const ativasNoGrupo = grupo.sessoes.filter((s) => s.ativo).length;
              const aberto = estaAberto(grupo.usuarioId);
              const collapseId = `sessoes-usuario-${grupo.usuarioId}`;
              return (
                <div key={grupo.usuarioId} className="card">
                  <div
                    className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2"
                    onClick={() => alternarCard(grupo.usuarioId)}
                    style={{ cursor: 'pointer' }}
                    aria-expanded={aberto}
                    aria-controls={collapseId}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="small text-muted" aria-hidden>
                        {aberto ? '▼' : '▶'}
                      </span>
                      <strong>{grupo.username}</strong>
                      {grupo.nome && <span className="text-muted">{grupo.nome}</span>}
                      <span className="badge bg-primary">
                        {ativasNoGrupo}/{LIMITE_SESSOES_ATIVAS} ativas
                      </span>
                      <span className="badge bg-secondary">
                        {grupo.sessoes.length} registro(s)
                      </span>
                    </div>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleEncerrarTodasSessoes(grupo.usuarioId);
                      }}
                      disabled={ativasNoGrupo === 0 || encerrando === grupo.usuarioId || encerrandoSistema}
                      title="Encerrar todas as sessões ativas deste usuário"
                    >
                      {encerrando === grupo.usuarioId ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                      ) : (
                        <BiLogOut size={18} className="me-1" />
                      )}
                      Encerrar ativas
                    </button>
                  </div>
                  <Collapse in={aberto}>
                    <div id={collapseId}>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover mb-0">
                          <thead>
                            <tr>
                              <th>IP</th>
                              <th>Login em</th>
                              <th>Último Acesso</th>
                              <th>Logout em</th>
                              <th>Status</th>
                              <th className="text-center">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grupo.sessoes.map((sessao) => (
                              <tr key={sessao.id}>
                                <td>
                                  <span title={tituloIpUserAgent(sessao)}>{sessao.ipOrigem || '-'}</span>
                                </td>
                                <td>{formatarData(sessao.logadoEm)}</td>
                                <td>{formatarData(sessao.ultimoAcesso)}</td>
                                <td>{formatarData(sessao.deslogadoEm)}</td>
                                <td>
                                  <span className={`badge ${sessao.ativo ? 'bg-success' : 'bg-secondary'}`}>
                                    {sessao.ativo ? 'Ativa' : 'Encerrada'}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {sessao.ativo ? (
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => void handleEncerrarSessao(sessao.id)}
                                      disabled={encerrando === sessao.id || encerrandoSistema}
                                      title="Encerrar esta sessão"
                                    >
                                      {encerrando === sessao.id ? (
                                        <span className="spinner-border spinner-border-sm" role="status" />
                                      ) : (
                                        <BiXCircle size={18} />
                                      )}
                                    </button>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        </>
      )}

      <ModalHistoricoSessoesUsuario
        show={modalHistoricoAberto}
        onHide={() => setModalHistoricoAberto(false)}
      />
    </div>
  );
}
